import Vue from 'vue'
import Router from 'vue-router'
import Login from '@/components/user/Login'
import Profile from '@/components/user/Profile'
import UserSMTP from '@/components/user/UserSMTP'
import ProfileCalendar from '@/components/user/ProfileCalendar'
import WhatsAppAutoReply from '@/components/user/WhatsAppAutoReply'
import ProfileSettings from '@/components/user/ProfileSettings'
import ProfileExtensions from '@/components/user/ProfileExtensions'
import store from '../store'

import AppTabs from '@/components/common/AppTabs'
import Create from '@/components/common/Create'
import Edit from '@/components/common/Edit'
import Show from '@/components/common/Show'
import Crud from '@/components/common/Crud'
import { trans } from '@/components/utilities/trans.js'
import { isTabAllowed } from '@/components/utilities/tabGates.js'

import { dashboard } from '@/router/modules/dashboard.js'
import { calendar } from '@/router/modules/calendar.js'
import { crm } from '@/router/modules/crm.js'
import { opportunities } from '@/router/modules/opportunities.js'
import { quotations } from '@/router/modules/quotations.js'
import { orders } from '@/router/modules/orders.js'
import { ddts } from '@/router/modules/ddts.js'
import { documents } from '@/router/modules/documents.js'
import { projects } from '@/router/modules/projects.js'
import { timecards } from '@/router/modules/timecards.js'
import { products } from '@/router/modules/products.js'
import { assets } from '@/router/modules/assets.js'
import { vehicles } from '@/router/modules/vehicles.js'
import { hr } from '@/router/modules/hr.js'
import { credentials } from '@/router/modules/credentials.js'
import { tasks } from '@/router/modules/tasks.js'
import { subscriptions } from '@/router/modules/subscriptions.js'
import { agents } from '@/router/modules/agents.js'
import { tickets } from '@/router/modules/tickets.js'
import { users } from '@/router/modules/users.js'
import { settings } from '@/router/modules/settings.js'
import { analytics } from '@/router/modules/analytics.js'
import { callcenter } from '@/router/modules/callcenter.js'
import { mailmarketing } from '@/router/modules/mailmarketing.js'
import { whatsapp } from '@/router/modules/whatsapp.js'
import { banci } from '@/router/modules/banci.js'
import { marbec } from '@/router/modules/marbec.js'
import { agribios } from '@/router/modules/agribios.js'
import { helpdesk } from '@/router/modules/helpdesk.js'
import { portal } from '@/router/modules/portal.js'
import { compliance } from '@/router/modules/compliance.js'
import { drive } from '@/router/modules/drive.js'

import Notifications from '@/components/common/Notifications'
import ReleasesHistory from '@/components/common/ReleasesHistory'
import NotFound from '@/components/common/NotFound'


export { AppTabs, Crud, Edit, Show, Create, store, trans }

Vue.use(Router)

// Attende che il profilo utente (permessi inclusi) sia caricato prima di valutare
// i guard basati sui permessi: su un deep link (accesso diretto all'URL) i guard
// girano prima che USER_REQUEST abbia popolato lo store, negando l'accesso anche
// agli utenti autorizzati.
function whenUserReady() {
	return new Promise(resolve => {
		if (!store.getters.isAuthenticated || store.getters.isProfileLoaded) {
			resolve()
			return
		}
		const unwatch = store.watch(
			(state, getters) => getters.isProfileLoaded || !getters.isAuthenticated,
			ready => {
				if (ready) {
					unwatch()
					resolve()
				}
			}
		)
	})
}

// Redirect di accesso negato: mai verso /login per utenti autenticati, perché
// la rotta /login reindirizza gli autenticati creando un loop di redirect
// infinito (RangeError e app bloccata sul loader).
function denyAccess(next) {
	next(store.getters.isAuthenticated ? '/' : '/login')
}

export function ifNotAuthenticated(to, from, next) {
	if (!store.getters.isAuthenticated) {
		next()
		return
	}
	next('/')
}

export function ifAuthenticated(to, from, next) {
	if (store.getters.isAuthenticated) {
		next()
		return
	}
	if (from.name == 'login') {
		store.commit('endProgress')
	}
	next('/login')
}

export function ifAuthenticatedOnline(to, from, next) {
	if (store.getters.isOnline && store.getters.isAuthenticated) {
		next()
		return
	}
	next('/login')
}

export function ifHrEmployee(to, from, next) {
	whenUserReady().then(() => {
		const profile = store.getters.getProfile || {}
		// In modalità "solo HR" I miei dati è la home di tutti: chi non è
		// collegato a un dipendente ci arriva e vede il banner di HrMe
		if (store.getters.isAuthenticated && profile.hr_module_enabled && (profile.hr_employee_id || store.getters.isHrOnly)) {
			next()
			return
		}
		denyAccess(next)
	})
}

// Dashboard: in modalità "solo HR" rimanda a I miei dati
export function ifDashboard(to, from, next) {
	if (!store.getters.isOnline || !store.getters.isAuthenticated) {
		next('/login')
		return
	}
	whenUserReady().then(() => {
		if (store.getters.isHrOnly) {
			next('/hrme')
			return
		}
		next()
	})
}

export function ifAuthenticatedOffline(to, from, next) {
	if (!store.getters.isOnline && store.getters.isAuthenticated) {
		next()
		return
	}
	next('/login')
}

export function hasPermissions(state, next) {
	whenUserReady().then(() => {
		if (store.getters.checkModelPermissions(state)) {
			next()
			return
		}
		denyAccess(next)
	})
}

export function hasModule(state, next) {
	whenUserReady().then(() => {
		if (store.getters.checkModulePermissions(state)) {
			next()
			return
		}
		denyAccess(next)
	})
}

export function canAccess(type, key, next) {
	whenUserReady().then(() => {
		switch(type) {

			case 'module':
				if (store.getters.checkModulePermissions(key)) {
					next()
					return
				}
				break

			case 'permission':
				if (store.getters.checkModelPermissions(key)) {
					next()
					return
				}
				break

			case 'setting':
				if (store.getters.checkSettings(key)) {
					next()
					return
				}
				break

			// permesso del ruolo, key = [model, action] (es. ['settings', 'index'])
			case 'role':
				if (store.getters.checkRolePermissions(key[0], key[1])) {
					next()
					return
				}
				break
		}
		// Accesso negato
		denyAccess(next)
	})
}

/**
 * Input:  array tabs (le tab della sezione), object tab (quella della rotta)
 * Fa:     guard di rotta della tab; senza permesso ripiega sulla prima tab
 *         consentita della sezione invece di aprire una pagina che risponde 403
 * Output: navigation guard (to, from, next)
 */
export function tabGuard(tabs, tab) {
	return (to, from, next) => {
		// Tab non nell'elenco (rotta rinominata e elenco non aggiornato): si lascia
		// passare come faceva ifAuthenticated, invece di negare per un errore di
		// configurazione. Il padre della sezione ha comunque il suo beforeEnter.
		if (!tab) {
			next()
			return
		}
		whenUserReady().then(() => {
			if (isTabAllowed(tab, store.getters)) {
				next()
				return
			}
			const fallback = tabs.find(t => isTabAllowed(t, store.getters))
			if (fallback) {
				// replace e non push: la tab negata non deve restare in cronologia,
				// altrimenti il Back ci torna sopra e rimbalza di nuovo qui.
				// query e hash non si trasportano: sono i parametri del report
				// negato e su un'altra tab non vogliono dire niente.
				next({ path: fallback.to, replace: true })
				return
			}
			denyAccess(next)
		})
	}
}

export default new Router({
	routes: [
		...dashboard,
		...calendar,
		...crm,
		...opportunities,
		...quotations,
		...orders,
		...ddts,
		...documents,
		...projects,
		...timecards,
		...products,
		...assets,
		...vehicles,
		...hr,
		...credentials,
		...tasks,
		...subscriptions,
		...agents,
		...tickets,
		...users,
		...settings,
		...analytics,
		...mailmarketing,
		...whatsapp,
		...callcenter,
		...banci,
		...marbec,
		...agribios,
		...helpdesk,
		...portal,
		...compliance,
		...drive,
		{
			path: '/404',
			name: 'notfound',
			component: NotFound,
			beforeEnter: ifAuthenticated
		},
		{
			path: '/',
			redirect: '/dashboard',
			beforeEnter: ifAuthenticated
		},
		{
			path: '/login',
			name: 'login',
			component: Login,
			beforeEnter: ifNotAuthenticated
		},
		{
			path: '/impersonate',
			name: 'impersonate',
			component: () => import('@/components/user/Impersonate.vue'),
		},
		{
			path: '/sso/callback',
			name: 'ssocallback',
			component: () => import('@/components/user/SsoCallback.vue'),
		},
		{
			path: '/profile/',
			name: 'profile',		
			beforeEnter: ifAuthenticatedOnline,
			component: AppTabs,
			props: {
				tabs: [
					{
						to: '/profile/',
						text: trans('profile')
					},
					{
						to: '/profile/templates-signature/',
						text: trans('mail_signatures')
					},
					{
						to: '/profile/smtp/',
						text: 'SMTP',
					},
					{
						to: '/profile/calendars/',
						text: trans('calendars'),
						module: 'calendar'
					},
					{
						to: '/profile/whatsapp-auto-reply/',
						text: 'Auto-Reply WhatsApp',
						module: 'whatsapp'
					},
					{
						to: '/profile/userdatas/',
						text: 'Preferenze',
					},
					{
						to: '/profile/settings/',
						text: 'Impostazioni',
					},
					{
						to: '/profile/download/',
						text: 'Download',
					},
					{
						to: '/profile/extensions/',
						text: 'Estensioni',
					},
				]
			},
			children: [
				{
					path: '/profile/',
					meta: {
						title: trans('profile')
					},	
					beforeEnter: ifAuthenticatedOnline,
					component: Profile,
					props: {
						modelDef: 'profile',
						actionIcon: 'account_circle',
						title: 'Profilo'
					}
				},
				{
					path: '/profile/smtp/',
					meta: {
						title: trans('mail_signatures')
					},
					beforeEnter: ifAuthenticatedOnline,
					component: UserSMTP,
					props: {
						actionIcon: 'dynamic_feed',
						title: 'Firme Mail'
					}
				},
				{
					path: '/profile/templates-signature/',
					meta: {
						title: trans('mail_signatures')
					},
					beforeEnter: ifAuthenticatedOnline,
					component: Crud,
					props: {
						modelDef: 'templates-signature',
						actionIcon: 'dynamic_feed',
						title: 'Firme Mail'
					}
				},
				{
					path: '/profile/calendars',
					meta: {
						title: trans('calendars')
					},
					beforeEnter: ifAuthenticatedOnline,
					component: ProfileCalendar
				},
				{
					path: '/profile/whatsapp-auto-reply/',
					meta: {
						title: 'Auto-Reply WhatsApp'
					},
					beforeEnter: ifAuthenticatedOnline,
					component: WhatsAppAutoReply
				},
				{
					path: '/profile/userdatas/',
					meta: {
						title: 'Preferenze'
					},
					beforeEnter: ifAuthenticatedOnline,
					component: Crud,
					props: {
						modelDef: 'userdatas',
						actionIcon: 'mdi-account-cog-outline',
						title: 'Preferenze',
						crudDialog: true,
					}
				},
				{
					path: '/profile/settings/',
					meta: {
						title: 'Impostazioni'
					},
					beforeEnter: ifAuthenticatedOnline,
					component: ProfileSettings,
				},
				{
					path: '/profile/download/',
					name: 'profile-download',
					meta: {
						title: 'Download'
					},
					beforeEnter: ifAuthenticatedOnline,
					component: Crud,
					props: {
						modelDef: 'archives',
						actionIcon: 'mdi-download',
						title: 'Download'
					}
				},
				{
					path: '/profile/extensions/',
					meta: {
						title: 'Estensioni'
					},
					beforeEnter: ifAuthenticatedOnline,
					component: ProfileExtensions,
				},
			]
		},
		{
			path: '/profile/templates-signature/create',
			name: 'templates-signature-create',
			beforeEnter: ifAuthenticatedOnline,
			component: Create,
			props: route => ({
				modelDef: 'templates-signature',
				redirect: route.params.redirect,
				defaultInput: {type: 'signature'},
			})
		},
		{
			path: '/profile/templates-signature/:id/edit',
			name: 'templates-signature-edit',
			beforeEnter: ifAuthenticatedOnline,
			component: Edit,
			props: route => ({
				modelDef: 'templates-signature',
				id: parseInt(route.params.id),
				defaultInput: {type: 'signature'},
			})
		},
		{ 
			path: '/profile/templates-signature/:id',
			name: 'templates-signature-show',
			beforeEnter: ifAuthenticatedOnline,
			component: Show,
			props: (route) => ({
				id: parseInt(route.params.id),
				modelDef:  'templates-signature',
				modelClass:  'template',
				activities: true
			}), 
		},			
		{
			path: '/notifications/',
			name: 'notifications',
			beforeEnter: ifAuthenticatedOnline,
			component: Notifications,
			meta: { noTabs: true }
		},
		{
			path: '/releases-history',
			name: 'releases-history',
			beforeEnter: ifAuthenticated,
			component: ReleasesHistory,
			meta: { noTabs: true }
		},
		// Vecchi indirizzi dell'archivio: i File Condivisi sono confluiti nel
		// Drive, cosi' i link salvati continuano a funzionare.
		{
			path: '/archive',
			redirect: '/drive/'
		},
		{
			path: '/archive/library',
			redirect: '/drive/'
		},
		{
			path: '/companies/shared-files',
			redirect: '/drive/'
		},
		{
			path: '*',
			redirect: '/404'
		}
	]
})
